import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { formatMoney } from '../../utils/format';

/**
 * What reception fills in to send someone through to the doctor: why they came,
 * today's vitals and the money taken at the desk.
 *
 * Lives on its own because two screens need it - the registration form, where a
 * new patient is queued in the same click, and the "Send to Doctor" dialog used
 * for someone already on the books.
 */

export const EMPTY_VISIT = {
  reason: '',
  weight: '',
  bpSystolic: '',
  bpDiastolic: '',
  pulse: '',
  temperature: '',
  spo2: '',
  fee: '',
  paid: '',
  mode: 'cash',
};

const VITAL_FIELDS = ['weight', 'bpSystolic', 'bpDiastolic', 'pulse', 'temperature', 'spo2'];

function number(value) {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Turns the typed-in values into the body POST /api/visits expects. */
export function toVisitPayload(visit) {
  const vitals = {};
  VITAL_FIELDS.forEach((field) => {
    const n = number(visit[field]);
    if (n !== undefined) vitals[field] = n;
  });

  return {
    reasonForVisit: (visit.reason || '').trim(),
    vitals,
    fees: {
      consultation: number(visit.fee) || 0,
      paid: number(visit.paid) || 0,
      mode: visit.mode || 'cash',
    },
  };
}

export default function VisitDetailsFields({ value, onChange, showWeight = true }) {
  const set = (field, next) => onChange({ ...value, [field]: next });
  const balance = Math.max(0, Number(value.fee || 0) - Number(value.paid || 0));

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Reason for visit / what the patient says</Form.Label>
        <Form.Control
          value={value.reason}
          onChange={(e) => set('reason', e.target.value)}
          placeholder="e.g. Fever and cough since 3 days"
        />
      </Form.Group>

      <div className="fw-semibold small text-uppercase text-muted mb-2">
        Today's Vitals
        <span className="text-muted fw-normal text-lowercase ms-2">leave blank what you did not measure</span>
      </div>
      <Row className="g-2 mb-3">
        {showWeight && (
          <Col xs={6} md={3}>
            <Form.Label>Weight</Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                step="0.1"
                value={value.weight}
                onChange={(e) => set('weight', e.target.value)}
              />
              <InputGroup.Text>kg</InputGroup.Text>
            </InputGroup>
          </Col>
        )}

        <Col xs={6} md={3}>
          <Form.Label>Blood Pressure</Form.Label>
          <InputGroup>
            <Form.Control
              type="number"
              placeholder="120"
              value={value.bpSystolic}
              onChange={(e) => set('bpSystolic', e.target.value)}
            />
            <InputGroup.Text className="px-2">/</InputGroup.Text>
            <Form.Control
              type="number"
              placeholder="80"
              value={value.bpDiastolic}
              onChange={(e) => set('bpDiastolic', e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col xs={6} md={2}>
          <Form.Label>Pulse</Form.Label>
          <InputGroup>
            <Form.Control type="number" value={value.pulse} onChange={(e) => set('pulse', e.target.value)} />
            <InputGroup.Text>/min</InputGroup.Text>
          </InputGroup>
        </Col>

        <Col xs={6} md={2}>
          <Form.Label>Temperature</Form.Label>
          <InputGroup>
            <Form.Control
              type="number"
              step="0.1"
              value={value.temperature}
              onChange={(e) => set('temperature', e.target.value)}
            />
            <InputGroup.Text>F</InputGroup.Text>
          </InputGroup>
        </Col>

        <Col xs={6} md={2}>
          <Form.Label>SpO2</Form.Label>
          <InputGroup>
            <Form.Control type="number" value={value.spo2} onChange={(e) => set('spo2', e.target.value)} />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
        </Col>
      </Row>

      <div className="fw-semibold small text-uppercase text-muted mb-2">Fees</div>
      <Row className="g-2 align-items-end">
        <Col xs={6} md={3}>
          <Form.Label>Consultation</Form.Label>
          <InputGroup>
            <InputGroup.Text>&#8377;</InputGroup.Text>
            <Form.Control type="number" value={value.fee} onChange={(e) => set('fee', e.target.value)} />
          </InputGroup>
        </Col>

        <Col xs={6} md={3}>
          <Form.Label>Received Now</Form.Label>
          <InputGroup>
            <InputGroup.Text>&#8377;</InputGroup.Text>
            <Form.Control type="number" value={value.paid} onChange={(e) => set('paid', e.target.value)} />
          </InputGroup>
        </Col>

        <Col xs={6} md={3}>
          <Form.Label>Mode</Form.Label>
          <Form.Select value={value.mode} onChange={(e) => set('mode', e.target.value)}>
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </Form.Select>
        </Col>

        <Col xs={6} md={3}>
          <div className="bg-light rounded px-3 py-2 text-center">
            <div className="vital-label">Balance</div>
            <strong className={balance > 0 ? 'text-danger' : 'text-success'}>{formatMoney(balance)}</strong>
          </div>
        </Col>
      </Row>
    </>
  );
}
